/**
 * Dialog - Reusable modal/dialog component
 * Modern dialog with title bar, close button, dividers, and action buttons
 */

import type { Component, ReactNode, MouseEvent as ReactMouseEvent } from 'react';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

interface DialogProps {
    title: string;
    children: ReactNode;
    onClose: () => void;
    actionButtons?: ReactNode;
    className?: string;
    closeOnBackdropClick?: boolean;
    maxWidth?: string;
}

interface DialogState {
    isVisible: boolean;
}

class Dialog extends ReactComponent<DialogProps, DialogState> {
    private backdropRef: HTMLDivElement | null = null;
    private contentRef: HTMLDivElement | null = null;
    private zIndex: number = 1000;

    constructor(props: DialogProps) {
        super(props);
        this.state = {
            isVisible: true
        };
    }

    componentDidMount(): void {
        // Add escape key handler
        document.addEventListener('keydown', this.handleEscapeKey);
        
        // Set z-index to be higher than existing dialogs
        // Use setTimeout to ensure the backdrop element is in the DOM
        setTimeout(() => {
            if (this.backdropRef) {
                const allBackdrops = document.querySelectorAll('.modern-dialog-backdrop');
                let maxZIndex = 1000;
                for (let i = 0; i < allBackdrops.length; i++) {
                    const backdrop = allBackdrops[i] as HTMLElement;
                    if (backdrop === this.backdropRef) continue;
                    const zIndex = parseInt(window.getComputedStyle(backdrop).zIndex) || 1000;
                    if (zIndex > maxZIndex) {
                        maxZIndex = zIndex;
                    }
                }
                this.zIndex = maxZIndex + 1;
                this.backdropRef.style.zIndex = String(this.zIndex);
            }
        }, 0);
    }

    componentWillUnmount(): void {
        document.removeEventListener('keydown', this.handleEscapeKey);
    }

    handleEscapeKey = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
            this.handleClose();
        }
    };

    handleClose = (): void => {
        this.setState({ isVisible: false });
        setTimeout(() => {
            this.props.onClose();
        }, 150); // Allow fade-out animation
    };

    handleBackdropClick = (e: ReactMouseEvent<HTMLDivElement>): void => {
        // Only close if clicking the backdrop itself, not the content
        if (e.target === this.backdropRef && this.props.closeOnBackdropClick !== false) {
            // Don't close if there are action buttons (user requirement)
            if (!this.props.actionButtons) {
                // Check if there are other dialogs open on top of this one
                // If any other dialog backdrop exists and the click is within it, don't close this dialog
                const allBackdrops = document.querySelectorAll('.modern-dialog-backdrop');
                let hasDialogOnTop = false;
                
                const clickEvent = e.nativeEvent as MouseEvent;
                const clickX = clickEvent.clientX;
                const clickY = clickEvent.clientY;
                
                for (let i = 0; i < allBackdrops.length; i++) {
                    const backdrop = allBackdrops[i] as HTMLElement;
                    if (backdrop === this.backdropRef) continue;
                    
                    // Check if the other backdrop is visible
                    const backdropStyle = window.getComputedStyle(backdrop);
                    if (backdropStyle.display === 'none' || backdropStyle.visibility === 'hidden') {
                        continue;
                    }
                    
                    // Check if the click coordinates are within the other dialog's content area
                    const otherContent = backdrop.querySelector('.modern-dialog-content') as HTMLElement;
                    if (otherContent) {
                        const rect = otherContent.getBoundingClientRect();
                        
                        if (clickX >= rect.left && clickX <= rect.right &&
                            clickY >= rect.top && clickY <= rect.bottom) {
                            hasDialogOnTop = true;
                            break;
                        }
                    }
                    
                    // Also check if the click is within the other backdrop itself
                    const backdropRect = backdrop.getBoundingClientRect();
                    if (clickX >= backdropRect.left && clickX <= backdropRect.right &&
                        clickY >= backdropRect.top && clickY <= backdropRect.bottom) {
                        // Check z-index to see if this other dialog is on top
                        const thisZIndex = parseInt(window.getComputedStyle(this.backdropRef!).zIndex) || 1000;
                        const otherZIndex = parseInt(backdropStyle.zIndex) || 1000;
                        
                        if (otherZIndex >= thisZIndex) {
                            hasDialogOnTop = true;
                            break;
                        }
                    }
                }
                
                if (!hasDialogOnTop) {
                    this.handleClose();
                }
            }
        }
    };

    render(): ReactNode {
        if (!this.state.isVisible) {
            return null;
        }

        return React.createElement('div', {
            ref: (el: HTMLDivElement) => { this.backdropRef = el; },
            className: `modern-dialog-backdrop ${this.props.className || ''}`,
            onClick: this.handleBackdropClick
        },
            React.createElement('div', {
                ref: (el: HTMLDivElement) => { this.contentRef = el; },
                className: 'modern-dialog-content',
                onClick: (e: any) => e.stopPropagation(),
                style: this.props.maxWidth ? { maxWidth: this.props.maxWidth } : undefined
            },
                // Title Bar
                React.createElement('div', { className: 'modern-dialog-header' },
                    React.createElement('h3', { className: 'modern-dialog-title' }, this.props.title),
                    React.createElement('button', {
                        className: 'modern-dialog-close',
                        onClick: this.handleClose,
                        'aria-label': 'Close'
                    }, '×')
                ),
                // Divider
                React.createElement('hr', { className: 'modern-dialog-divider' }),
                // Content
                React.createElement('div', {
                    className: `modern-dialog-body ${this.props.className?.includes('no-padding') ? 'no-padding' : ''}`,
                    style: this.props.className?.includes('no-padding') ? { padding: 0 } : undefined
                },
                    this.props.children
                ),
                // Action Buttons (if provided)
                this.props.actionButtons && React.createElement(React.Fragment, null,
                    React.createElement('hr', { className: 'modern-dialog-divider' }),
                    React.createElement('div', { className: 'modern-dialog-footer' },
                        this.props.actionButtons
                    )
                )
            )
        );
    }
}

// Make available globally
(window as any).Dialog = Dialog;

export default Dialog;
